import { useEffect, useRef, useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function ContactSection() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch('https://readdy.ai/api/form/d7677oflpdqvlupfpq30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const trustPoints = [
    { icon: 'ri-shield-check-line', key: 'contact.trust1' },
    { icon: 'ri-time-line', key: 'contact.trust2' },
    { icon: 'ri-customer-service-2-line', key: 'contact.trust3' },
  ];

  return (
    <section
      id="contact"
      ref={ref}
      className="relative overflow-hidden py-24 md:py-36 px-4 md:px-8 bg-brand-white"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-1 bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* Left: copy */}
          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 mb-5">
              <div className="w-8 h-px bg-gold" />
              <span className="font-body text-xs text-gold tracking-widest uppercase font-semibold">{t('contact.badge')}</span>
            </div>
            <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-brand-black leading-tight mb-6">
              {t('contact.title1')}{' '}
              <span className="text-gold">{t('contact.title2')}</span>
            </h2>
            <p className="font-body text-base text-brand-black/60 leading-relaxed mb-8">
              {t('contact.intro')}
            </p>

            {/* Trust points */}
            <div className="space-y-4">
              {trustPoints.map((pt) => (
                <div key={pt.key} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${pt.icon} text-xl text-gold`} />
                  </div>
                  <span className="font-body text-sm text-brand-black/70">{t(pt.key)}</span>
                </div>
              ))}
            </div>

            {/* Image block */}
            <div className="mt-10 rounded-2xl overflow-hidden w-full h-52 md:h-64">
              <img
                src="https://public.readdy.ai/ai/img_res/d8e53c58ec1e80372366c9c8a9d107bf.jpg"
                alt="Church LED installation"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right: form */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="bg-brand-black rounded-2xl p-8 md:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[radial-gradient(circle,rgba(212,175,55,0.12),transparent_70%)]" />

              {status === 'success' ? (
                <div className="relative z-10 flex flex-col items-center justify-center text-center py-12 gap-4">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gold/20">
                    <i className="ri-checkbox-circle-line text-4xl text-gold" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-white">{t('contact.success.title')}</h3>
                  <p className="font-body text-sm text-brand-white/60">{t('contact.success.desc')}</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-4 font-body text-sm text-gold border border-gold/40 px-6 py-2 rounded-full hover:bg-gold/10 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('contact.success.another')}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="relative z-10 space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body text-xs text-brand-white/60 tracking-wide uppercase">{t('contact.label.name')} *</label>
                      <input
                        name="full_name"
                        type="text"
                        required
                        placeholder={t('contact.placeholder.name')}
                        className="bg-brand-white/[0.06] border border-brand-white/[0.1] text-brand-white placeholder-brand-white/30 rounded-lg px-4 py-3 text-sm font-body focus:outline-none focus:border-gold/60 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body text-xs text-brand-white/60 tracking-wide uppercase">{t('contact.label.church')} *</label>
                      <input
                        name="church_name"
                        type="text"
                        required
                        placeholder={t('contact.placeholder.church')}
                        className="bg-brand-white/[0.06] border border-brand-white/[0.1] text-brand-white placeholder-brand-white/30 rounded-lg px-4 py-3 text-sm font-body focus:outline-none focus:border-gold/60 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-xs text-brand-white/60 tracking-wide uppercase">{t('contact.label.email')} *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder={t('contact.placeholder.email')}
                      className="bg-brand-white/[0.06] border border-brand-white/[0.1] text-brand-white placeholder-brand-white/30 rounded-lg px-4 py-3 text-sm font-body focus:outline-none focus:border-gold/60 transition-colors"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-xs text-brand-white/60 tracking-wide uppercase">{t('contact.label.phone')} *</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder={t('contact.placeholder.phone')}
                      className="bg-brand-white/[0.06] border border-brand-white/[0.1] text-brand-white placeholder-brand-white/30 rounded-lg px-4 py-3 text-sm font-body focus:outline-none focus:border-gold/60 transition-colors"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body text-xs text-brand-white/60 tracking-wide uppercase">{t('contact.label.city')} *</label>
                      <input
                        name="city_state"
                        type="text"
                        required
                        placeholder={t('contact.placeholder.city')}
                        className="bg-brand-white/[0.06] border border-brand-white/[0.1] text-brand-white placeholder-brand-white/30 rounded-lg px-4 py-3 text-sm font-body focus:outline-none focus:border-gold/60 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body text-xs text-brand-white/60 tracking-wide uppercase">{t('contact.label.screen_area')}</label>
                      <input
                        name="screen_area"
                        type="text"
                        placeholder={t('contact.placeholder.screen_area')}
                        className="bg-brand-white/[0.06] border border-brand-white/[0.1] text-brand-white placeholder-brand-white/30 rounded-lg px-4 py-3 text-sm font-body focus:outline-none focus:border-gold/60 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-xs text-brand-white/60 tracking-wide uppercase">{t('contact.label.message')}</label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      placeholder={t('contact.placeholder.message')}
                      className="bg-brand-white/[0.06] border border-brand-white/[0.1] text-brand-white placeholder-brand-white/30 rounded-lg px-4 py-3 text-sm font-body focus:outline-none focus:border-gold/60 transition-colors resize-none"
                    />
                  </div>

                  {status === 'error' && (
                    <p className="font-body text-xs text-red-400">{t('contact.error')}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full font-heading font-bold text-sm bg-gold text-brand-black py-4 rounded-full hover:bg-gold-light transition-all duration-300 cursor-pointer whitespace-nowrap mt-2 disabled:opacity-60"
                    style={{ boxShadow: '0 0 25px rgba(212,175,55,0.35)' }}
                  >
                    {status === 'loading' ? t('contact.sending') : t('contact.submit')}
                  </button>

                  <p className="font-body text-xs text-center text-brand-white/40 mt-2">
                    {t('contact.trust_note')}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
